import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Menu,
  Dropdown,
  Button,
  Checkbox,
  Tag,
  Tooltip,
  Select,
  Switch,
  Input,
  Space,
} from "antd";
import CustomDateRange from "./CustomDateRange";
import {
  DownOutlined,
  MoreOutlined,
  SearchOutlined,
  CloseOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import {
  isUserRoleAdd,
  isUserManagementAdd,
  isOrganization,
  isUserRoleDelete,
  isUserManagementDelete,
} from "../../utils/permissions";
import { API_ROUTES, Roles } from "../../utils/constants";
import "./header.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getProfileDetails } from "../../redux/slices/profileSlice";
import { resetChatSearch, getChatData } from "../../redux/slices/chatSlice";
import SearchBar from "./SearchBar";
import { toast } from "react-toastify";
import AnalyticsService from "../../services/api/analyticsService";
import { getAllTemplates } from "../../redux/slices/broadcastTemplateSlice";

const { Header } = Layout;

const TopHeader = ({
  dropdownItems,
  isTitle,
  title,
  isRightContent,
  icon,
  onChangeOptions,
  onChangeAllOptions,
  allChecked,
  openAddPopup,
  openDeletePopup,
  titlePrefix,
  dropdownName,
  isRightButton,
  buttonText,
  tooltipText,
  onRightBtnClick,
  rightBtnIcon,
  showDeleteSelection,
  onDeleteConfirmation,
  showDeleteButton,
  showSearchButton,
  showColumnDropdown = true,
  showDateRange = false,
  dateRange,
  onDateRangeSelect,
  onDateRangeChange,
  manualMode,
  onManualModeChange,
  startDate,
  endDate,
  onCustomRangeChange,
}) => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 576);
  const [searchOpen, setSearchOpen] = useState(false);
  const [localManualMode, setLocalManualMode] = useState(manualMode || false);
  const [localDateRange, setLocalDateRange] = useState(
    dateRange || "Last 30 Days",
  );
  const [isExportLoading, setIsExportLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleResize = useCallback(() => {
    setIsSmallScreen(window.innerWidth < 576);
  }, []);

  const { chatPage } = useSelector((state) => state.chatReducer);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const renderColumnMenu = () => {
    const columnEntries = Object.entries(dropdownItems || {}).filter(
      ([column]) => column !== "sNo",
    );
    const hasVisibleColumns = columnEntries.some(([, isVisible]) => isVisible);
    const allColumnsChecked =
      columnEntries.length > 0 &&
      columnEntries.every(([, isVisible]) => isVisible);

    return (
      <Menu>
        <Menu.Item key="all" className="parent-menu-item-all">
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={allColumnsChecked}
              indeterminate={!allColumnsChecked && hasVisibleColumns}
              onChange={onChangeAllOptions}
            >
              Select All
            </Checkbox>
          </div>
        </Menu.Item>

        {columnEntries.map(([column, isVisible]) => (
          <Menu.Item key={column} style={{ marginLeft: "10px" }}>
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isVisible}
                onChange={() => onChangeOptions(column)}
              >
                {column.charAt(0).toUpperCase() + column.slice(1)}
              </Checkbox>
            </div>
          </Menu.Item>
        ))}
      </Menu>
    );
  };

  const renderMenu = () => (
    <Menu>
      {addPermission && openAddPopup && (
        <Menu.Item>
          <Button className="add-btn" onClick={openAddPopup}>
            {title === "User Groups" ? "Create Group" : `Add ${title}`}
          </Button>
        </Menu.Item>
      )}
      {deletePermission && openDeletePopup && showDeleteButton && (
        <Menu.Item>
          <Button className="delete-btn" onClick={openDeletePopup}>
            Delete Selected
          </Button>
        </Menu.Item>
      )}
      {isRightContent && showColumnDropdown && (
        <Menu.Item>
          <Dropdown overlay={renderColumnMenu()} trigger={["click"]}>
            <Button className="columns-btn">
              Columns <DownOutlined />
            </Button>
          </Dropdown>
        </Menu.Item>
      )}
    </Menu>
  );

  const addPermission =
    (title === "User Role" && isUserRoleAdd) ||
    (title === "User" && isUserManagementAdd) ||
    (title === "Organization" && isOrganization === Roles.SUPER_ADMIN) ||
    title === "User Groups";

  const deletePermission =
    (title === "User Role" && isUserRoleDelete) ||
    (title === "User" && isUserManagementDelete) ||
    (title === "Organization" && isOrganization === Roles.SUPER_ADMIN) ||
    title === "User Groups";

  const onOpenSearchClick = () => {
    if (searchOpen) {
      dispatch(resetChatSearch());
      dispatch(getChatData({ navigate, page: 1 }));
    } else {
      dispatch(resetChatSearch());
    }
    setSearchOpen(!searchOpen);
  };

  const getDateRangeDates = (range) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (range) {
      case "Last 24 Hours":
        // startDate.setHours(startDate.getHours() - 24);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "Last 7 Days":
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "Last 30 Days":
        startDate.setDate(startDate.getDate() - 30);
        break;
      case "Last 90 Days":
        startDate.setDate(startDate.getDate() - 90);
        break;
      default:
        startDate.setDate(startDate.getDate() - 30);
    }

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
  };

  const handleDateRangeChange = (value) => {
    setLocalDateRange(value);
    if (onDateRangeSelect) {
      onDateRangeSelect(value);
    }
    if (!localManualMode && onDateRangeChange) {
      const dates = getDateRangeDates(value);
      onDateRangeChange(dates.startDate, dates.endDate);
    }
  };

  const handleManualModeToggle = (checked) => {
    setLocalManualMode(checked);
    if (onManualModeChange) {
      onManualModeChange(checked);
    }
  };

  const handleExportAndMailAnalyticsReport = async () => {
    setIsExportLoading(true);
    try {
      const analyticsService = new AnalyticsService();
      const url = `${API_ROUTES.ANALYTICS.EXPORT_DASHBOARD_REPORT}?startDate=${startDate}&endDate=${endDate}`;
      const response = await analyticsService.instance.post(
        url,
        {},
        {
          responseType: "arraybuffer",
          headers: { Accept: "application/pdf" },
        },
      );
      const contentType =
        response.headers?.["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: contentType });
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "SimplifyPath_Analytics_Report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);

      const message =
        response.headers?.["x-message"] || response.headers?.["X-Message"];
      if (message) toast.success(message);
    } catch (err) {
      console.error("Export/download failed:", err);
      toast.error("Failed to download report");
    } finally {
      setIsExportLoading(false);
    }
  };

  useEffect(() => {
    if (manualMode !== undefined) {
      setLocalManualMode(manualMode);
    }
  }, [manualMode]);

  useEffect(() => {
    if (dateRange !== undefined) {
      setLocalDateRange(dateRange);
    }
  }, [dateRange]);

  const handleApiCall = (page, limit, searchKey = "") => {
    dispatch(getAllTemplates({ page, limit, name: searchKey }));
  };

  return (
    <Header className="sticky-header">
      <div className="top-header-content">
        <div className="header-left">
          <div className="display-flex-center-gap-10">
            {icon}
            <div className="text-white">
              <div className="display-flex-center-gap-10">
                {isTitle && (
                  <div
                    className={
                      isSmallScreen
                        ? "header-title-text-small"
                        : "header-title-text"
                    }
                  >
                    {title} {titlePrefix ? titlePrefix : null}
                  </div>
                )}
                {!isSmallScreen && addPermission && openAddPopup && (
                  <Tag
                    style={{ padding: "5px 10px", cursor: "pointer" }}
                    onClick={openAddPopup}
                  >
                    {title === "User Groups" ? "Create Group" : `Add ${title}`}
                  </Tag>
                )}
                {!isSmallScreen &&
                  deletePermission &&
                  openDeletePopup &&
                  showDeleteButton && (
                    <Tag
                      style={{ padding: "5px 10px", cursor: "pointer" }}
                      onClick={openDeletePopup}
                    >
                      Delete Selected
                    </Tag>
                  )}
                {showDeleteSelection && (
                  <Tag
                    style={{ padding: "5px 10px", cursor: "pointer" }}
                    onClick={onDeleteConfirmation}
                  >
                    Delete Selection
                  </Tag>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="header-right">
          {title === "Template" && (
            <Button onClick={() => handleApiCall(1, 10)}>Refresh</Button>
          )}

          {title === "Analytics" && (
            <Button
              onClick={handleExportAndMailAnalyticsReport}
              style={{ marginRight: "10px" }}
              loading={isExportLoading}
              className="export-btn"
              icon={<ExportOutlined />}
            >
              Export and Email
            </Button>
          )}
          {showDateRange && (
            <div className="analytics-date-filter-wrap">
              <Space size="middle" style={{ marginRight: "10px" }}>
                {!localManualMode ? (
                  <Select
                    value={localDateRange}
                    onChange={handleDateRangeChange}
                    style={{ width: 150 }}
                    className="date-range-select"
                  >
                    <Select.Option value="Last 24 Hours">Today</Select.Option>
                    <Select.Option value="Last 7 Days">
                      Last 7 Days
                    </Select.Option>
                    <Select.Option value="Last 30 Days">
                      Last 30 Days
                    </Select.Option>
                    <Select.Option value="Last 90 Days">
                      Last 90 Days
                    </Select.Option>
                  </Select>
                ) : (
                  <CustomDateRange
                    startDate={startDate}
                    endDate={endDate}
                    onRangeChange={onCustomRangeChange}
                  />
                )}
                <Tooltip title={localManualMode ? "Date Range" : "Manual Date"}>
                  <Switch
                    checked={localManualMode}
                    onChange={handleManualModeToggle}
                    className="manual-date-toggle"
                  />
                </Tooltip>
              </Space>
            </div>
          )}

          {isSmallScreen && addPermission ? (
            <Dropdown overlay={renderMenu()} trigger={["click"]}>
              <Button className="more-btn">
                <MoreOutlined />
              </Button>
            </Dropdown>
          ) : (
            <>
              {isRightButton && (
                <Tooltip title={tooltipText}>
                  <Button onClick={onRightBtnClick}>
                    <div style={{ display: "flex", gap: "5px" }}>
                      <div>{rightBtnIcon}</div>
                      <div>{buttonText}</div>
                    </div>
                  </Button>
                </Tooltip>
              )}
              {showSearchButton && (
                <Tooltip title={searchOpen ? "Cancel search" : "Search"}>
                  <Button
                    onClick={onOpenSearchClick}
                    style={{ marginLeft: "10px" }}
                  >
                    <div style={{ display: "flex", gap: "5px" }}>
                      {searchOpen ? <CloseOutlined /> : <SearchOutlined />}
                    </div>
                  </Button>
                </Tooltip>
              )}
              {isRightContent && showColumnDropdown && (
                <Dropdown overlay={renderColumnMenu()} trigger={["click"]}>
                  <Tag
                    style={{
                      padding: "5px 10px",
                      cursor: "pointer",
                      width: "75%",
                      marginLeft: "10px",
                    }}
                    className="columns-btn"
                  >
                    {dropdownName ? dropdownName : "Columns"} <DownOutlined />
                  </Tag>
                </Dropdown>
              )}
            </>
          )}
        </div>
      </div>
      <SearchBar isOpen={searchOpen} />
    </Header>
  );
};

export default TopHeader;
